import React, { useMemo, useState, useEffect } from 'react';
import { Card, Button, Alert, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FiCheck } from 'react-icons/fi';
import { IoPeople } from 'react-icons/io5';
import { toast } from 'react-toastify';
import { saveBillBatch } from './billParsingSlice';
import { LoadingState } from '../../utilities/constants';
import { parseUserInfo } from '../../utilities/common';
import { fetchEntityTree } from '../entity/entitySlice';
import { recursiveMap, recursiveFind } from '../../utilities/treeHelpers';
import TreeViewModal from '../../components/TreeViewModal';
import DeleteConfirmDialog from '../../components/DeleteConfirmDialog';
import useSimilarEntityDetection from './hooks/useSimilarEntityDetection';
import useMeterEditing from './hooks/useMeterEditing';
import useParentAssignment from './hooks/useParentAssignment';
import EntityCard from './components/EntityCard';
import AssignParentModal from './components/AssignParentModal';

const ParsedDataView = ({ parsedData, onCancel, onSuccess }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { orgId, usrRole } = parseUserInfo(useSelector((state) => state.users.user));
  const { batchSaveStatus, batchSaveResult } = useSelector((state) => state.billParsing);
  const entityTree = useSelector((state) => state.entities.tree);
  const entityStatus = useSelector((state) => state.entities.status);

  const isAdmin = ['cv_root', 'cv_admin'].includes(usrRole);

  const [groupedMeters, setGroupedMeters] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDeleteEntityModal, setShowDeleteEntityModal] = useState(false);
  const [deleteEntityTarget, setDeleteEntityTarget] = useState(null);
  const [savingEntityIndexes, setSavingEntityIndexes] = useState(new Set());
  const [showEntitySelector, setShowEntitySelector] = useState(false);
  const [entitySelectorGroupIndex, setEntitySelectorGroupIndex] = useState(null);
  const [inactiveEntities, setInactiveEntities] = useState([]);
  const [showInactiveModal, setShowInactiveModal] = useState(false);

  useEffect(() => {
    if (entityStatus === LoadingState.idle) {
      dispatch(fetchEntityTree({ orgId }));
    }
  }, [entityStatus, orgId, dispatch]);

  const { similarEntityMatches, handleKeepExisting, handleUseNew, renameEntityInMatches, updateSimilarMatch } =
    useSimilarEntityDetection(groupedMeters, entityTree);

  const { editingMeters, handleEdit, handleCancelEdit, handleFieldChange, handleSaveMeterEdit } = useMeterEditing(
    groupedMeters,
    setGroupedMeters
  );

  const {
    parentAssignments,
    showAssignParentModal,
    selectedEntitiesForParent,
    showParentSelector,
    getEntityParentInfo,
    handleOpenAssignParent,
    handleCloseAssignParent,
    handleDoneAssignParent,
    handleOpenParentSelector,
    handleCloseParentSelector,
    handleSelectParent,
    handleToggleEntityForParent
  } = useParentAssignment(orgId, entityTree);

  const findEntityByName = (entityName) => {
    if (!entityTree || !entityName) return null;
    return recursiveFind(entityTree, (node) => node.divName === entityName);
  };

  const getInactiveEntities = (groups) => {
    if (!groups || !entityTree) return [];
    const seen = new Set();
    return groups.reduce((inactive, group) => {
      if (!seen.has(group.entityName)) {
        seen.add(group.entityName);
        const entity = findEntityByName(group.entityName);
        if (entity && entity.divEnabled !== 'Y') {
          inactive.push({ name: entity.divName, id: entity.divId });
        }
      }
      return inactive;
    }, []);
  };

  const billsList = useMemo(() => {
    if (!parsedData) return null;
    const billsArray = Array.isArray(parsedData)
      ? parsedData
      : parsedData.results || parsedData.bills || parsedData.data || [];
    if (!Array.isArray(billsArray) || billsArray.length === 0) return null;
    return billsArray.filter((bill) => bill.parsedData?.metadata);
  }, [parsedData]);

  const groupedByEntity = useMemo(() => {
    if (!billsList) return null;
    const groups = {};

    billsList.forEach((bill) => {
      const { metadata, meters } = bill.parsedData;
      const entityName = metadata?.entityName;
      if (!entityName) return;

      if (!groups[entityName]) {
        groups[entityName] = { entityName, meters: [], seenMeterKeys: new Set() };
      }

      if (meters && Array.isArray(meters)) {
        meters.forEach((meter) => {
          const meterWithMetadata = {
            ...meter,
            invoiceNumber: metadata?.billNumber || '—',
            provider: metadata?.provider || '—',
            currency: metadata?.currency || '—',
            fileName: bill.fileName,
            viewUrl: bill.viewUrl,
            viewUrlExpiresAt: bill.viewUrlExpiresAt
          };
          const meterKey = JSON.stringify({
            invoiceNumber: meterWithMetadata.invoiceNumber,
            provider: meterWithMetadata.provider,
            currency: meterWithMetadata.currency,
            meterNumber: meterWithMetadata.meterNumber,
            utility: meterWithMetadata.utility,
            activityType: meterWithMetadata.activityType,
            periodStart: meterWithMetadata.periodStart,
            periodEnd: meterWithMetadata.periodEnd,
            contribution: meterWithMetadata.contribution,
            contributionUnit: meterWithMetadata.contributionUnit,
            cost: meterWithMetadata.cost
          });
          if (!groups[entityName].seenMeterKeys.has(meterKey)) {
            groups[entityName].seenMeterKeys.add(meterKey);
            groups[entityName].meters.push(meterWithMetadata);
          }
        });
      }
    });

    return Object.values(groups).map(({ entityName, meters }) => ({ entityName, meters }));
  }, [billsList]);

  useEffect(() => {
    if (groupedByEntity) {
      setGroupedMeters(JSON.parse(JSON.stringify(groupedByEntity)));
    }
  }, [groupedByEntity]);

  const displayGroups = groupedMeters || groupedByEntity;

  const batchLevelError = useMemo(() => {
    if (!batchSaveResult?.summary?.errors) return null;
    return batchSaveResult.summary.errors.find((err) => err.fileName === 'batch')?.error || null;
  }, [batchSaveResult]);

  const errorsByFileName = useMemo(() => {
    if (!batchSaveResult?.summary?.errors) return {};
    return batchSaveResult.summary.errors.reduce((map, err) => {
      if (err.fileName !== 'batch') map[err.fileName] = err.error;
      return map;
    }, {});
  }, [batchSaveResult]);

  const buildBillPayload = (bill, entityName, meters) => {
    const similarMatch = similarEntityMatches[entityName];
    const useExisting = similarMatch && (similarMatch.isExactMatch || similarMatch.action === 'keep');

    const metadata = { ...bill.parsedData.metadata, entityName };
    const payload = { tempS3Key: bill.tempS3Key, fileName: bill.fileName, parsedData: { metadata, meters } };

    if (useExisting && similarMatch.similarEntity?.divId) {
      payload.existingEntityId = similarMatch.similarEntity.divId;
    } else if (parentAssignments[entityName]) {
      payload.parentEntityId = parentAssignments[entityName].parentId;
    }

    return payload;
  };

  const handleSaveEntity = async (groupIndex, entityName) => {
    if (Object.keys(editingMeters).length > 0) {
      toast.error(t('Please save or cancel all edits before saving'));
      return;
    }

    const entityGroup = groupedMeters[groupIndex];
    if (!entityGroup || entityGroup.meters.length === 0) {
      toast.error(t('No data to save for this entity'));
      return;
    }

    const inactiveList = getInactiveEntities([entityGroup]);
    if (inactiveList.length > 0) {
      setInactiveEntities(inactiveList);
      setShowInactiveModal(true);
      return;
    }

    setSavingEntityIndexes(new Set([...savingEntityIndexes, groupIndex]));

    const updatedMeters = {};
    entityGroup.meters.forEach((meter) => {
      if (!updatedMeters[meter.fileName]) updatedMeters[meter.fileName] = [];
      updatedMeters[meter.fileName].push(meter);
    });

    const billsToSave = billsList
      .filter((bill) => updatedMeters[bill.fileName])
      .map((bill) => buildBillPayload(bill, entityGroup.entityName, updatedMeters[bill.fileName]));

    if (billsToSave.length === 0) {
      toast.error(t('No valid bills to save'));
      setSavingEntityIndexes((prev) => {
        const s = new Set(prev);
        s.delete(groupIndex);
        return s;
      });
      return;
    }

    const result = await dispatch(saveBillBatch({ orgId, bills: billsToSave }));
    setSavingEntityIndexes((prev) => {
      const s = new Set(prev);
      s.delete(groupIndex);
      return s;
    });

    if (result.meta.requestStatus === 'fulfilled' && (result.payload?.summary?.failedBills || 0) === 0) {
      const newGroups = [...groupedMeters];
      newGroups.splice(groupIndex, 1);
      setGroupedMeters(newGroups);
      if (newGroups.length === 0) onSuccess();
    }
  };

  const isSaving = batchSaveStatus === LoadingState.pending;

  const handleConfirmSave = async () => {
    if (Object.keys(editingMeters).length > 0) {
      toast.error(t('Please save or cancel all edits before submitting'));
      return;
    }
    if (!groupedMeters || groupedMeters.length === 0) {
      toast.error(t('No data to save'));
      return;
    }

    const inactiveList = getInactiveEntities(groupedMeters);
    if (inactiveList.length > 0) {
      setInactiveEntities(inactiveList);
      setShowInactiveModal(true);
      return;
    }

    const updatedMeters = {};
    const entityNameByFileName = {};
    groupedMeters.forEach((group) => {
      group.meters.forEach((meter) => {
        if (!updatedMeters[meter.fileName]) updatedMeters[meter.fileName] = [];
        updatedMeters[meter.fileName].push(meter);
        entityNameByFileName[meter.fileName] = group.entityName;
      });
    });

    const billsToSave = billsList
      .map((bill) => {
        const entityName = entityNameByFileName[bill.fileName] || bill.parsedData.metadata.entityName;
        return buildBillPayload(bill, entityName, updatedMeters[bill.fileName] || bill.parsedData.meters);
      })
      .filter((bill) => bill.parsedData.meters && bill.parsedData.meters.length > 0);

    if (billsToSave.length === 0) {
      toast.error(t('No valid bills to save'));
      return;
    }

    const result = await dispatch(saveBillBatch({ orgId, bills: billsToSave }));
    if (result.meta.requestStatus === 'fulfilled' && (result.payload?.summary?.failedBills || 0) === 0) {
      onSuccess();
    }
  };

  const handleDeleteClick = (groupIndex, meterIndex) => {
    setDeleteTarget({ groupIndex, meterIndex });
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    const { groupIndex, meterIndex } = deleteTarget;
    const newGroups = [...groupedMeters];
    newGroups[groupIndex].meters.splice(meterIndex, 1);
    if (newGroups[groupIndex].meters.length === 0) newGroups.splice(groupIndex, 1);
    setGroupedMeters(newGroups);
    if (newGroups.length === 0) toast.info(t('All data has been removed'));
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  const handleDeleteEntityClick = (groupIndex) => {
    setDeleteEntityTarget(groupIndex);
    setShowDeleteEntityModal(true);
  };

  const confirmDeleteEntity = () => {
    if (deleteEntityTarget === null) return;
    const newGroups = [...groupedMeters];
    newGroups.splice(deleteEntityTarget, 1);
    setGroupedMeters(newGroups);
    if (newGroups.length === 0) toast.info(t('All data has been removed'));
    setShowDeleteEntityModal(false);
    setDeleteEntityTarget(null);
  };

  const handleOpenEntitySelector = (groupIndex) => {
    setEntitySelectorGroupIndex(groupIndex);
    setShowEntitySelector(true);
  };

  const handleKeepExistingEntity = (entityName) => {
    const match = similarEntityMatches[entityName];
    if (!match?.similarEntity) return;

    handleKeepExisting(entityName);

    const newEntityName = match.similarEntity.divName;
    if (newEntityName !== entityName) {
      renameEntityInMatches(entityName, newEntityName);
      setGroupedMeters((prev) =>
        prev.map((g) => (g.entityName === entityName ? { ...g, entityName: newEntityName } : g))
      );
    }
  };

  const transformedTree = useMemo(() => {
    if (!entityTree) return {};
    const tree = recursiveMap(entityTree, (n) => ({
      value: n.divId,
      labelRaw: n.divName,
      label: (
        <span
          style={{ cursor: 'pointer' }}
          onClick={() => {
            if (entitySelectorGroupIndex !== null) {
              const oldEntityName = groupedMeters[entitySelectorGroupIndex].entityName;
              const newGroups = [...groupedMeters];
              newGroups[entitySelectorGroupIndex].entityName = n.divName;
              newGroups[entitySelectorGroupIndex].meters.forEach((m) => {
                m.entityName = n.divName;
              });
              setGroupedMeters(newGroups);
              setShowEntitySelector(false);
              setEntitySelectorGroupIndex(null);
              updateSimilarMatch(oldEntityName, n);
            }
          }}
        >
          {n.divName}
        </span>
      ),
      showCheckbox: true,
      valueId: n.divAssignedId
    }));
    tree.isRoot = true;
    return tree;
  }, [entityTree, entitySelectorGroupIndex, groupedMeters, updateSimilarMatch]);

  const transformedTreeForParent = useMemo(() => {
    if (!entityTree) return {};
    const tree = recursiveMap(entityTree, (n) => ({
      value: n.divId,
      labelRaw: n.divName,
      label: (
        <span style={{ cursor: 'pointer' }} onClick={() => handleSelectParent(n)}>
          {n.divName}
        </span>
      ),
      showCheckbox: true,
      valueId: n.divAssignedId
    }));
    tree.isRoot = true;
    return tree;
  }, [entityTree, handleSelectParent]);

  if (!billsList || billsList.length === 0) {
    return (
      <Card>
        <Card.Body>
          <Alert variant="warning">{t('No valid bills found in the uploaded files')}</Alert>
          <div className="d-flex justify-content-end">
            <Button variant="outline-secondary" onClick={onCancel}>
              {t('Cancel')}
            </Button>
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (!groupedByEntity || groupedByEntity.length === 0) {
    return (
      <Card>
        <Card.Body>
          <Alert variant="warning">{t('No valid data found in the uploaded files')}</Alert>
          <div className="d-flex justify-content-end">
            <Button variant="outline-secondary" onClick={onCancel}>
              {t('Cancel')}
            </Button>
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (!displayGroups || displayGroups.length === 0) {
    return (
      <Card>
        <Card.Body>
          <Alert variant="warning">{t('No data to display. All items have been removed.')}</Alert>
          <div className="d-flex justify-content-end">
            <Button variant="outline-secondary" onClick={onCancel}>
              {t('Cancel')}
            </Button>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-1">
            <h5 className="mb-0">{t('Batch Upload Results')}</h5>
            {isAdmin && (
              <Button variant="outline-dark" size="sm" onClick={handleOpenAssignParent}>
                <IoPeople className="mr-2" style={{ verticalAlign: 'middle' }} />
                {t('Assign Parent')}
              </Button>
            )}
          </div>
          <p className="text-muted mb-4" style={{ fontSize: '13px' }}>
            {t('Review extracted data from uploaded bills')}
          </p>

          {batchLevelError && (
            <Alert variant="danger" className="mb-4">
              <strong>{t('Validation Error')}:</strong> {batchLevelError}
            </Alert>
          )}

          {displayGroups.map((entityGroup, groupIndex) => (
            <EntityCard
              key={groupIndex}
              entityGroup={entityGroup}
              groupIndex={groupIndex}
              similarMatch={similarEntityMatches[entityGroup.entityName]}
              isEntitySaving={savingEntityIndexes.has(groupIndex)}
              error={errorsByFileName[entityGroup.meters[0]?.fileName]}
              editingMeters={editingMeters}
              onSaveEntity={handleSaveEntity}
              onDeleteEntity={handleDeleteEntityClick}
              onEditEntity={handleOpenEntitySelector}
              onKeepExisting={handleKeepExistingEntity}
              onUseNew={handleUseNew}
              onEdit={handleEdit}
              onSaveMeterEdit={handleSaveMeterEdit}
              onCancelEdit={handleCancelEdit}
              onDeleteClick={handleDeleteClick}
              onFieldChange={handleFieldChange}
            />
          ))}

          <div className="d-flex justify-content-end mt-4">
            <Button variant="outline-secondary" onClick={onCancel} disabled={isSaving} className="mr-2">
              {t('Cancel')}
            </Button>
            <Button variant="dark" onClick={handleConfirmSave} disabled={billsList.length === 0 || isSaving}>
              {isSaving ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2" />
                  {t('Saving...')}
                </>
              ) : (
                <>
                  <FiCheck className="mr-2" />
                  {t('Confirm & Save All')}
                </>
              )}
            </Button>
          </div>
        </Card.Body>
      </Card>

      <DeleteConfirmDialog
        visible={showDeleteModal}
        onDiscardHandler={() => {
          setShowDeleteModal(false);
          setDeleteTarget(null);
        }}
        onDeleteHandler={confirmDelete}
        headerTitle={t('Confirm Delete')}
      >
        {t('Are you sure you want to delete this row? This action cannot be undone.')}
      </DeleteConfirmDialog>

      <DeleteConfirmDialog
        visible={showDeleteEntityModal}
        onDiscardHandler={() => {
          setShowDeleteEntityModal(false);
          setDeleteEntityTarget(null);
        }}
        onDeleteHandler={confirmDeleteEntity}
        headerTitle={t('Confirm Delete')}
      >
        {t(
          'Are you sure you want to delete this entire entity section? This will remove all meter rows associated with this entity. This action cannot be undone.'
        )}
      </DeleteConfirmDialog>

      <TreeViewModal
        initExpandLevel={0}
        tree={transformedTree}
        closeModal={() => {
          setShowEntitySelector(false);
          setEntitySelectorGroupIndex(null);
        }}
        show={showEntitySelector}
        title={t('Select Entity')}
      />

      <TreeViewModal
        initExpandLevel={0}
        tree={transformedTreeForParent}
        closeModal={handleCloseParentSelector}
        show={showParentSelector}
        title={t('Select Parent Entity')}
      />

      <Modal show={showInactiveModal} onHide={() => setShowInactiveModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('Inactive Entities Detected')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('The following entities are inactive:')}</p>
          <Alert variant="light" className="mb-3">
            <ul className="mb-0" style={{ paddingLeft: '20px' }}>
              {inactiveEntities.map((entity, idx) => (
                <li key={idx}>{entity.name}</li>
              ))}
            </ul>
          </Alert>
          <p className="mb-0">{t('Please contact your system administrator to reactivate them before uploading.')}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowInactiveModal(false)}>
            {t('Close')}
          </Button>
        </Modal.Footer>
      </Modal>

      <AssignParentModal
        show={showAssignParentModal}
        displayGroups={displayGroups}
        selectedEntitiesForParent={selectedEntitiesForParent}
        onClose={handleCloseAssignParent}
        onSave={handleDoneAssignParent}
        onToggleEntity={handleToggleEntityForParent}
        onOpenParentSelector={handleOpenParentSelector}
        getEntityParentInfo={getEntityParentInfo}
      />
    </>
  );
};

export default ParsedDataView;
